import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { useRef } from "react";

import { Switch } from "@/components/Form/Base/switch.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useOutsideAlerter } from "@/utils/hooks/useOutsideAlerter";

type FeatureSwitchComponentProps = {
    isOpen: boolean;
    features: Array<{
        name: FeatureToggleKey;
        value: boolean;
        serverValue: boolean | null;
    }>;
    bottomControlsVisible: boolean;
    onToggle: (state?: boolean) => void;
    onToggleFeature: (name: FeatureToggleKey, value: boolean) => void;
};

const FeatureSwitchComponent = ({
    isOpen,
    features,
    bottomControlsVisible,
    onToggle,
    onToggleFeature
}: FeatureSwitchComponentProps) => {
    const { t } = useGranite();
    const ref = useRef<HTMLDivElement | null>(null);

    useOutsideAlerter(() => {
        onToggle(false);
    }, ref);

    return (
        <div
            className={cn("tw:fixed tw:left-4 tw:bottom-4", {
                "tw:bottom-16 tw:tw:md:bottom-16": bottomControlsVisible
            })}
            ref={ref}
        >
            <div
                className={cn(
                    "tw:mb-4 tw:border tw:border-grey-input tw:p-2 tw:pt-3 tw:rounded-md tw:w-[calc(100vw-4rem)] tw:flex-col tw:flex tw:gap-2",
                    {
                        "tw:hidden": !isOpen,
                        "tw:visible": isOpen
                    }
                )}
            >
                {features.map((feature) => {
                    return (
                        <div
                            key={feature.name}
                            className={cn(
                                "tw:flex tw:items-center tw:justify-between tw:gap-4"
                            )}
                        >
                            <Switch
                                name={feature.name}
                                value={feature.value}
                                label={t(`featureSwitch.${feature.name}`)}
                                onToggle={() =>
                                    onToggleFeature(
                                        feature.name,
                                        !feature.value
                                    )
                                }
                            />
                            {isDefined(feature.serverValue) && (
                                <div
                                    className={cn(
                                        "tw:flex tw:items-center tw:gap-2"
                                    )}
                                >
                                    <span className={cn("tw:block")}>
                                        Server value
                                    </span>
                                    <span
                                        className={cn(
                                            "tw:max-w-12.5 tw:text-white tw:rounded-sm tw:text-sm tw:px-1",
                                            {
                                                "bg-success":
                                                    feature.serverValue,
                                                "bg-danger":
                                                    !feature.serverValue
                                            }
                                        )}
                                    >
                                        {String(feature.serverValue)}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <Button
                type={"button"}
                color={"warning"}
                onClick={() => onToggle()}
            >
                <i className={cn("las la-sliders-h")} />
            </Button>
        </div>
    );
};

export { FeatureSwitchComponent };
